import { useContext, useEffect, useState } from "react";

import Spinner from "../../../../shared/components/spinner/spinner";
import AuthContext from "../../../../shared/context/auth-context";
import useHttp from "../../../../shared/hooks/use-http";
import { deleteMultiple, getAllData, postMultiple } from "../../../../shared/utils/api";
import { validateLoadingStatusNew } from "../../../../shared/utils/utils";
import ControlledTable from "../../../components/controlled-table/controlled-table";
import { IColumn, ISortedBy, ITableRowData } from "../../../components/data-table/data-table";
import Popup from "../../../components/popup/popup";
import { noop } from "../../../utils";
import "./notes.scss";
import NotesExpandedView from "./notes-expanded-view";
import { BsGrid3X3Gap, BsListUl } from "react-icons/bs";

export const notesDisplayColumns: IColumn[] = [
  {
    key: "title",
    displayName: "Title",
    dataType: {
      type: "text",
    },
  },
  {
    key: "content",
    displayName: "Content",
    dataType: {
      type: "textarea",
      size: "large",
    },
  },
  {
    key: "createdByName",
    displayName: "Created By",
    dataType: {
      type: "text",
    },
    sortable: false,
  },
  {
    key: "createdAt",
    displayName: "Created At",
    dataType: {
      type: "dateTime",
    },
    sortable: false,
  },
];

export const notesColumns: IColumn[] = [
  {
    key: "title",
    displayName: "Title",
    dataType: {
      type: "text",
    },
  },
  {
    key: "content",
    displayName: "Content",
    dataType: {
      type: "textarea",
      size: "large",
    },
  },
];

interface INotesProps {
  alertcaseId: string;
}

const Notes = ({ alertcaseId }: INotesProps) => {
  const authCtx = useContext(AuthContext);

  const [sortedByCol, setSortedByCol] = useState<ISortedBy>({
    column: "title",
    direction: "desc",
  });
  const [data, setData] = useState<ITableRowData[]>([]);
  const [selectedRow, setSelectedRow] = useState<ITableRowData | undefined>(undefined);
  const [showModal, setShowModal] = useState(false);
  const [isExpandedView, setIsExpandedView] = useState(false);
  const [isUserReadOnly, setIsUserReadOnly] = useState(false);
  const { sendRequest: getTableData, status: getTableDataStatus, data: getTableDataResult, error: getTableDataError } = useHttp(getAllData, true, authCtx.token, "note", "manager");
  const { sendRequest: postData, status: postDataStatus, data: postDataResult, error: postDataError } = useHttp(postMultiple, true, authCtx.token, "note", "manager");
  const { sendRequest: deleteMultipleRequest, status: deleteMultipleStatus, data: deleteMultipleData, error: deleteMultipleError } = useHttp(deleteMultiple, false, authCtx.token, "note", "manager");

  useEffect(() => {
    if (!alertcaseId) {
      return;
    }
    getTableData(
      `alertcase/${alertcaseId}/notes?order_by=${sortedByCol.column} ${sortedByCol.direction}`
    );
  }, [sortedByCol, alertcaseId]);

  useEffect(() => {
    const roles = authCtx.selectedTenant.roles || [];
    setIsUserReadOnly(roles.length === 1 && roles[0] === "Read");
  }, [authCtx.selectedTenant]);

  useEffect(() => {
    if (getTableDataStatus === "completed" && !getTableDataError && getTableDataResult) {
      setData(getTableDataResult);
    }
  }, [getTableDataStatus, getTableDataError, getTableDataResult]);

  useEffect(() => {
    if (postDataStatus === "completed" && !postDataError && postDataResult) {
      setData(postDataResult);
    }
  }, [postDataStatus, postDataError, postDataResult]);

  useEffect(() => {
    if (deleteMultipleStatus === "completed" && !deleteMultipleError && deleteMultipleData) {
      setData(deleteMultipleData);
    }
  }, [deleteMultipleStatus, deleteMultipleError, deleteMultipleData]);

  const handleOnRowClick = (id: string) => {
    const rowClicked = data.find((row) => row.id === id);
    if (rowClicked) {
      setSelectedRow(rowClicked);
      setShowModal(true);
    }
  };

  const handleOnDelete = (ids: string[]) => {
    deleteMultipleRequest(
      `alertcase/${alertcaseId}/notes?order_by=${sortedByCol.column} ${sortedByCol.direction}`,
      { ids: ids }
    );
  };

  const handleModalSubmit = (result: ITableRowData | undefined) => {
    setShowModal(false);
    if (!result) {
      return;
    }
    postData(
      `alertcase/${alertcaseId}/notes?order_by=${sortedByCol.column} ${sortedByCol.direction}`,
      result
    );
    setSelectedRow(undefined);
  };

  return (
    <div className="notes">
      {validateLoadingStatusNew([getTableDataStatus]) && alertcaseId && (
        <Spinner />
      )}
      <Popup
        mode={"Note"}
        buttonText={"Save"}
        columns={notesColumns}
        values={selectedRow}
        show={showModal}
        handlePopupClosed={handleModalSubmit}
      />
      <div className="notes-toggleWrapper">
        <div className="notes-segmentedControl">
          <button
            className={!isExpandedView ? "active" : ""}
            onClick={() => setIsExpandedView(false)}
          >
            <BsGrid3X3Gap size={16} />
            <span>Table</span>
          </button>
          <button
            className={isExpandedView ? "active" : ""}
            onClick={() => setIsExpandedView(true)}
          >
            <BsListUl size={16} />
            <span>Expanded</span>
          </button>
        </div>
      </div>

      {isExpandedView ? (
        <div className="notes-expandedContainer">
          <NotesExpandedView
            data={data}
            handleEditNote={(note) => {
              setSelectedRow(note);
              setShowModal(true);
            }}
            isUserReadOnly={isUserReadOnly}
          />
        </div>
      ) : (
        <ControlledTable
          data={data}
          onDeleted={handleOnDelete}
          onSorted={setSortedByCol}
          onNewClicked={() => setShowModal(true)}
          onRowClick={handleOnRowClick}
          onScroll={noop}
          selectionMode="multi"
          columns={notesDisplayColumns}
          readOnly={isUserReadOnly}
          allowCollapsedText={true}
        />
      )}
    </div>
  );
};

export default Notes;
